import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Check, CheckCircle2, Circle, ExternalLink, Eye, EyeOff, Loader2, MinusCircle, RefreshCw, Trash2, Undo2,
} from 'lucide-react';
import {
  AUTO_RESOLVE_BATCH_STATUSES, AUTO_RESOLVE_BATCH_ACTIONS, AUTO_RESOLVE_BATCH_RESULTS,
} from '../../config/autoResolve';
import {
  STEP_LABELS, batchSteps, batchRequestRows, safeHttpUrl, previewPageUrl, asArray, formatWhen, plural,
  UUIDISH, CHIP, TONE, BUCKET_META, STATUS_META, RESULT_META, NOT_REPORTED, DISCARDABLE, FINAL, FOLD_META,
  SECTION_HEADING, FOCUS, PRIMARY_BTN, SECONDARY_BTN, QUIET_BTN, DANGER_BTN, ERROR_BOX, EXTERNAL_LINK,
} from './autoResolveBatchShared';

// The per-batch detail of batch auto-resolve (docs/auto-resolve-batch.md §3,
// EXPERIMENTAL): status and stepper, preview / PR / run links, every
// request's result (with the agent's question), preview deep links, and
// Refresh, Discard and the two-click Merge & publish. One component for the
// per-school panel (AutoResolveBatchPanel) and the all-schools "Batch runs"
// strip (AutoResolveBatchStrip), so both render a batch identically.
//
// Presentational: the parent owns the API calls. `onAction(kind, batchId)`
// returns a promise of true when the action went through. Everything the
// agent wrote (summaries, questions, pages) is untrusted text: rendered as
// text only, and links are built only from http(s) URLs.

const { PR_OPEN } = AUTO_RESOLVE_BATCH_STATUSES;
const { DONE, NEEDS_HUMAN, SKIPPED } = AUTO_RESOLVE_BATCH_RESULTS;

// The classification chip (auto / review / human). `short` labels the human
// bucket "Person" (Edit Requests rows, the bulk dialog) instead of "For a
// person" (the per-school panel).
export function BucketChip({ bucket, reason, short = false, className = '', testId = 'auto-resolve-chip' }) {
  const meta = BUCKET_META[bucket];
  if (!meta) return null;
  return (
    <span className={`${CHIP} ${TONE[meta.tone]} ${className}`} title={reason || undefined} data-testid={testId} data-bucket={bucket}>
      <span className="sr-only">Batch auto-resolve: </span>{short ? meta.short : meta.label}
      {reason ? <span className="sr-only"> ({reason})</span> : null}
    </span>
  );
}

// A batch's status as a chip (the same labels as the card's pill).
export function BatchStatusChip({ status, testId = 'batch-status-pill' }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', tone: 'grey' };
  return <span className={`${CHIP} ${TONE[meta.tone]}`} data-testid={testId}>{meta.label}</span>;
}

// Why a batch is folded out of the attention queue, when its status chip
// doesn't already say it (Dismissed, Superseded — partitionBatches).
export function FoldReasonChip({ reason, testId = 'batch-fold-reason' }) {
  const meta = FOLD_META[reason];
  if (!meta?.reasonChip) return null;
  return (
    <span className={`${CHIP} ${TONE.quiet}`} title={`${meta.label}: ${meta.why}.`} data-testid={testId} data-reason={reason}>
      {meta.label}<span className="sr-only"> ({meta.why})</span>
    </span>
  );
}

// Dismiss (out of the attention queue) or Restore (back into it). The
// visible word stays while it's busy, so the accessible name always holds it.
export function DismissButton({ restore = false, school, busy = false, disabled = false, onClick, id, testId }) {
  const Icon = busy ? Loader2 : restore ? Undo2 : EyeOff;
  const who = school ? `${school}’s batch` : 'this batch';
  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={restore ? `Restore ${who} to the queue` : `Dismiss ${who} from the queue`}
      title={restore ? 'Put it back in the queue.' : 'Take it out of the queue. Nothing else changes; Show all lists it, with Restore.'}
      className={QUIET_BTN}
      data-testid={testId || (restore ? 'batch-restore' : 'batch-dismiss')}
    >
      <Icon className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" /> {restore ? 'Restore' : 'Dismiss'}
    </button>
  );
}

// requestInfo(id) → { title, page } | null. Without it, the batch's own
// `requests` ([{ id, title, page }] from the API) name each request.
// `headerExtra` (optional) sits beside the status chip — the per-school
// panel puts the fold reason and Dismiss / Restore there.
export default function BatchCard({
  batch, school, requestInfo, busyKind, anyBusy, error, onAction, as: Tag = 'li', headingLevel = 4, headerExtra = null,
}) {
  const [confirm, setConfirm] = useState(null); // 'merge' | 'discard' | null
  const [mergeSent, setMergeSent] = useState(false);
  const confirmRef = useRef(null);
  const mergeRef = useRef(null);
  const discardRef = useRef(null);
  const headingRef = useRef(null);
  const returnFocusTo = useRef(null);

  const status = batch.status;
  const rows = batchRequestRows(batch);
  const doneCount = rows.filter((r) => r.status === DONE).length;
  const runHref = safeHttpUrl(batch.run_url);
  const prHref = safeHttpUrl(batch.pr_url);
  const previewHref = safeHttpUrl(batch.preview_url);
  const canMerge = status === PR_OPEN && !mergeSent;
  const canDiscard = DISCARDABLE.includes(status) && !mergeSent;
  const canRefresh = !FINAL.includes(status);
  const by = batch.created_by && !UUIDISH.test(String(batch.created_by)) ? String(batch.created_by) : '';
  const schoolName = school || 'this school';
  const Heading = `h${Math.min(Math.max(Number(headingLevel) || 4, 2), 6)}`;

  const ownInfo = new Map();
  for (const r of asArray(batch.requests)) if (r?.id != null) ownInfo.set(String(r.id), r);
  const infoFor = (id) => (requestInfo ? requestInfo(id) : null) || ownInfo.get(String(id)) || null;

  // Focus follows the two-step confirm: onto Confirm when it opens, back to
  // the button that opened it when cancelled.
  useEffect(() => {
    if (confirm) {
      confirmRef.current?.focus();
    } else if (returnFocusTo.current) {
      (returnFocusTo.current === 'merge' ? mergeRef : discardRef).current?.focus();
      returnFocusTo.current = null;
    }
  }, [confirm]);

  // A confirm only applies to the state it was opened in.
  useEffect(() => {
    if (confirm === 'merge' && status !== PR_OPEN) setConfirm(null);
    if (confirm === 'discard' && !DISCARDABLE.includes(status)) setConfirm(null);
  }, [status, confirm]);

  const cancel = () => {
    returnFocusTo.current = confirm;
    setConfirm(null);
  };

  // The confirm buttons go away once the action is sent: focus moves to the
  // batch's heading instead of getting lost.
  const merge = async () => {
    const ok = await onAction(AUTO_RESOLVE_BATCH_ACTIONS.MERGE, batch.id);
    setConfirm(null);
    if (ok) setMergeSent(true);
    headingRef.current?.focus();
  };

  const discard = async () => {
    await onAction(AUTO_RESOLVE_BATCH_ACTIONS.DISCARD, batch.id);
    setConfirm(null);
    headingRef.current?.focus();
  };

  return (
    <Tag
      className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden"
      data-testid={`batch-${batch.id}`}
      data-status={status}
    >
      <div className="px-4 pt-3.5 pb-3 border-b border-white/[0.06]">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <Heading ref={headingRef} tabIndex={-1} className="text-[13px] font-semibold text-white leading-tight focus:outline-none">
              Batch of {plural(rows.length, 'request')}
            </Heading>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {[formatWhen(batch.created_at) && `Started ${formatWhen(batch.created_at)}`, by && `by ${by}`,
                batch.merged_at && `published ${formatWhen(batch.merged_at)}`].filter(Boolean).join(' · ')}
            </p>
          </div>
          {headerExtra ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {headerExtra}
              <BatchStatusChip status={status} />
            </div>
          ) : <BatchStatusChip status={status} />}
        </div>
        <BatchStepper batch={batch} />
      </div>

      {(runHref || prHref || previewHref || status === PR_OPEN) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 border-b border-white/[0.06] text-[11px]">
          {previewHref ? (
            <a
              href={previewHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#B0FC38]/50 bg-[#B0FC38]/[0.06] text-[#B0FC38] text-[12px] font-semibold hover:bg-[#B0FC38]/[0.12] hover:border-[#B0FC38] transition-all ${FOCUS}`}
            >
              <Eye className="w-3.5 h-3.5" aria-hidden="true" /> View preview
              <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
            </a>
          ) : status === PR_OPEN ? (
            <span className="inline-flex items-center gap-1.5 text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Building the preview…
            </span>
          ) : null}
          {prHref && (
            <a href={prHref} target="_blank" rel="noopener noreferrer" className={EXTERNAL_LINK}>
              {batch.pr_number ? `Change #${batch.pr_number}` : 'Pull request'} on GitHub
              <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
            </a>
          )}
          {runHref && (
            <a href={runHref} target="_blank" rel="noopener noreferrer" className={EXTERNAL_LINK}>
              Agent run
              <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
            </a>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <div className={`${SECTION_HEADING} mb-2`}>Requests</div>
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <BatchResultRow key={row.id} row={row} info={infoFor(row.id)} previewUrl={previewHref} />
            ))}
          </ul>
        </div>
      )}

      <div className="px-4 py-3 flex flex-col gap-2.5">
        {error && <div className={ERROR_BOX} role="alert">{error}</div>}

        {mergeSent && status === PR_OPEN && (
          <p className="inline-flex items-center gap-2 text-[12px] text-amber-200/90" data-testid="batch-merging">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" aria-hidden="true" />
            Publishing… waiting for GitHub to confirm the merge.
          </p>
        )}

        {confirm === 'merge' && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200/90" data-testid="batch-merge-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" aria-hidden="true" />
            <span>
              This publishes {doneCount ? plural(doneCount, 'change') : 'this batch’s changes'} to the LIVE {schoolName} website
              right away. Visitors see them within a few minutes. Each request marked done is resolved and the school gets
              the usual email; requests that need a person stay open.
            </span>
          </div>
        )}

        {confirm === 'discard' && (
          <div className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.08] text-[12px] text-gray-300" data-testid="batch-discard-warning">
            Discard this batch? {prHref ? 'This closes the draft change, deletes its branch and puts' : 'This puts'} its{' '}
            {plural(rows.length, 'request')} back to Open. Nothing on the live site changes.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          {canRefresh && !confirm && (
            <button
              type="button"
              onClick={() => onAction(AUTO_RESOLVE_BATCH_ACTIONS.REFRESH, batch.id)}
              disabled={anyBusy}
              aria-label="Refresh this batch from GitHub"
              className={`${SECONDARY_BTN} mr-auto`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busyKind === AUTO_RESOLVE_BATCH_ACTIONS.REFRESH ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
            </button>
          )}

          {confirm === 'discard' ? (
            <>
              <button type="button" onClick={cancel} className={`text-[12px] text-gray-400 hover:text-white rounded ${FOCUS}`}>Keep it</button>
              <button
                ref={confirmRef}
                type="button"
                onClick={discard}
                disabled={anyBusy}
                className={DANGER_BTN}
              >
                {busyKind === AUTO_RESOLVE_BATCH_ACTIONS.DISCARD
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Discarding…</>
                  : <><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Confirm: discard batch</>}
              </button>
            </>
          ) : confirm === 'merge' ? (
            <>
              <button type="button" onClick={cancel} className={`text-[12px] text-gray-400 hover:text-white rounded ${FOCUS}`}>Cancel</button>
              <button
                ref={confirmRef}
                type="button"
                onClick={merge}
                disabled={anyBusy}
                aria-label={`Confirm publishing this batch to the live ${schoolName} website`}
                className={PRIMARY_BTN}
              >
                {busyKind === AUTO_RESOLVE_BATCH_ACTIONS.MERGE
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Publishing…</>
                  : <><Check className="w-3.5 h-3.5" aria-hidden="true" /> Confirm: publish to live site</>}
              </button>
            </>
          ) : (
            <>
              {canDiscard && (
                <button ref={discardRef} type="button" onClick={() => setConfirm('discard')} disabled={anyBusy} className={QUIET_BTN}>
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Discard
                </button>
              )}
              {canMerge && (
                <button
                  ref={mergeRef}
                  type="button"
                  onClick={() => setConfirm('merge')}
                  disabled={anyBusy}
                  aria-label={`Merge & publish this batch to the ${schoolName} website (asks to confirm)`}
                  className={PRIMARY_BTN}
                >
                  <Check className="w-3.5 h-3.5" aria-hidden="true" /> Merge &amp; publish
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Tag>
  );
}

const STEP_STATE_TEXT = {
  complete: 'done',
  current: 'in progress',
  upcoming: 'not started',
  skipped: 'not reached',
  failed: 'stopped',
  needs_person: 'stopped',
  discarded: 'stopped',
};

export function BatchStepper({ batch }) {
  const steps = batchSteps(batch);
  return (
    <ol className="flex items-center gap-1.5" aria-label="Batch progress" data-testid={`batch-stepper-${batch.id}`}>
      {steps.map((s, i) => (
        <React.Fragment key={STEP_LABELS[i]}>
          <li className="flex items-center gap-1.5 min-w-0" data-step={STEP_LABELS[i]} data-state={s.state} aria-current={s.state === 'current' ? 'step' : undefined}>
            <StepIcon state={s.state} />
            <span
              className={`text-[11px] truncate ${
                s.state === 'failed' ? 'text-red-300'
                  : s.state === 'needs_person' ? 'text-amber-200'
                    : s.state === 'discarded' ? 'text-gray-500'
                      : s.state === 'complete' ? 'text-gray-300'
                        : s.state === 'current' ? 'text-[#B0FC38] font-medium'
                          : 'text-gray-600'
              }`}
            >
              {s.label}
              <span className="sr-only"> ({STEP_STATE_TEXT[s.state]})</span>
            </span>
          </li>
          {i < steps.length - 1 && (
            <li aria-hidden="true" className={`h-px flex-1 min-w-[8px] ${s.state === 'complete' ? 'bg-[#B0FC38]/40' : 'bg-white/[0.08]'}`} />
          )}
        </React.Fragment>
      ))}
    </ol>
  );
}

function StepIcon({ state }) {
  if (state === 'complete') return <CheckCircle2 className="w-4 h-4 text-[#B0FC38] shrink-0" aria-hidden="true" />;
  if (state === 'current') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border-2 border-[#B0FC38] shadow-[0_0_8px_rgba(176,252,56,0.6)] shrink-0" aria-hidden="true">
        <span className="w-1.5 h-1.5 rounded-full bg-[#B0FC38]" />
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500/15 border border-red-500/40 shrink-0" aria-hidden="true">
        <AlertTriangle className="w-2.5 h-2.5 text-red-300" />
      </span>
    );
  }
  if (state === 'needs_person') return <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" aria-hidden="true" />;
  if (state === 'discarded') return <MinusCircle className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />;
  return <Circle className="w-4 h-4 text-gray-700 shrink-0" aria-hidden="true" />;
}

function BatchResultRow({ row, info, previewUrl }) {
  const r = row.result || {};
  const meta = RESULT_META[row.status] || RESULT_META[NOT_REPORTED];
  const title = info?.title || `Request ${String(row.id).slice(0, 8)}`;
  const pages = asArray(r.pages).filter((p) => typeof p === 'string' && p);
  const question = r.question || r.reason || '';
  return (
    <li className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5 flex flex-col gap-1.5" data-testid={`batch-result-${row.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-white break-words">{title}</span>
        {info?.page && <span className="text-[11px] font-mono text-gray-500 break-all">{info.page}</span>}
        <span className={`${CHIP} ${TONE[meta.tone]} ml-auto`} data-testid="batch-result-status">{meta.label}</span>
      </div>

      {r.summary && <p className="text-[12px] text-gray-300 break-words">{r.summary}</p>}

      {row.status === NEEDS_HUMAN && (
        <p className="rounded-lg border border-amber-500/25 bg-amber-500/[0.05] px-3 py-2 text-[12px] break-words" data-testid="batch-result-question">
          <span className="font-medium text-amber-200">The agent asks: </span>
          <span className="text-gray-100">{question || 'It didn’t say what it needs.'}</span>
        </p>
      )}
      {row.status === SKIPPED && r.reason && (
        <p className="text-[12px] text-gray-400 break-words">Skipped: {r.reason}</p>
      )}
      {row.status === NOT_REPORTED && (
        <p className="text-[12px] text-gray-400">The agent didn’t report on this request; it stays open for a person.</p>
      )}

      {pages.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-gray-500">{pages.length === 1 ? 'Page' : 'Pages'}</span>
          {pages.map((p) => {
            const href = previewPageUrl(previewUrl, p);
            return href ? (
              <a
                key={p}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.03] border border-white/[0.08] font-mono text-gray-300 hover:text-white hover:border-white/[0.25] ${FOCUS}`}
              >
                {p}
                <ExternalLink className="w-3 h-3" aria-hidden="true" /><span className="sr-only">(preview, opens in a new tab)</span>
              </a>
            ) : (
              <span key={p} className="px-2 py-0.5 rounded-md bg-white/[0.03] border border-white/[0.08] font-mono text-gray-400 break-all">{p}</span>
            );
          })}
        </div>
      )}
    </li>
  );
}
